import { observer } from 'mobx-react';
import { FC, Fragment, ReactNode, TouchEvent, TouchList, useRef, useState } from 'react';

import { Button, Card, Chip, Flex, FlexProps, Grid, Text } from '@servicetitan/anvil2';
import IconAdd from '@servicetitan/anvil2/assets/icons/material/round/add.svg';
import { core } from '@servicetitan/anvil2/token';
import { useDependencies } from '@servicetitan/react-ioc';

import { MainStore } from '../../common/stores/main.store';
import { ReplacementPartViewModelWithMatch } from '../models/replacement-part-view-model';

export interface ReplacementCardProps {
    replacementPart: ReplacementPartViewModelWithMatch;
    isLoadingPricebookMatches: boolean;
    onSelect: (item: ReplacementPartViewModelWithMatch) => void;
}

export const ReplacementCard: FC<ReplacementCardProps> = observer(
    ({ replacementPart, isLoadingPricebookMatches, onSelect }) => {
        const [mainStore] = useDependencies<[MainStore]>(MainStore);

        const [modalOpen, setModalOpen] = useState(false);
        const [scale, setScale] = useState(1);
        const [lastTouchDistance, setLastTouchDistance] = useState<number | null>(null);
        const imgRef = useRef<HTMLImageElement>(null);

        // Pinch-to-zoom handlers
        const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
            if (e.touches.length === 2) {
                const dist = getTouchDistance(e.touches);
                setLastTouchDistance(dist);
            }
        };

        const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
            if (e.touches.length === 2 && lastTouchDistance) {
                const dist = getTouchDistance(e.touches);
                const newScale = Math.max(1, Math.min(4, scale * (dist / lastTouchDistance)));
                setScale(newScale);
                setLastTouchDistance(dist);
            }
        };

        const handleTouchEnd = () => {
            setLastTouchDistance(null);
        };

        function getTouchDistance(touches: TouchList) {
            const [touch1, touch2] = [touches[0], touches[1]];
            return Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
        }

        const handleCardClick = () => {
            onSelect(replacementPart);
        };

        let matchQuality: string = replacementPart.matchQuality;
        if (replacementPart.match) {
            if (replacementPart.match.quality === 0) {
                matchQuality = 'Low';
            } else if (replacementPart.match.quality === 1) {
                matchQuality = 'Medium';
            } else if (replacementPart.match.quality === 2) {
                matchQuality = 'High';
            }
        }

        const hasAnyPermissions =
            mainStore.hasPermissionToAddInvoiceItem ||
            mainStore.hasPermissionToAddEstimateItem ||
            mainStore.hasPermissionToAddNote;

        return (
            <Fragment>
                <Card className="w-100-i">
                    <Flex direction="column" gap="2" grow={1}>
                        <Flex direction="row" gap="4" alignItems="center" grow={1}>
                            <Card padding="xsmall">
                                <img
                                    alt={replacementPart.label}
                                    src={replacementPart.imageUrl}
                                    style={{ height: '35px', cursor: 'pointer' }}
                                    onClick={() => {
                                        setModalOpen(true);
                                        setScale(1);
                                    }}
                                />
                            </Card>
                            <Flex direction="column" grow={1} gap="1" justifySelf="stretch">
                                <Text
                                    variant="eyebrow"
                                    size="small"
                                    style={{ color: core.primitive.ColorNeutral500.value }}
                                >
                                    {replacementPart.brand}
                                </Text>
                                <Text size="small">#{replacementPart.partNumber}</Text>
                            </Flex>
                        </Flex>

                        {isLoadingPricebookMatches && (
                            <Text size="small">Loading procebook...</Text>
                        )}

                        {replacementPart.match ? (
                            <Fragment>
                                <Text variant="eyebrow" size="small">
                                    {replacementPart.match.type === 3
                                        ? 'MATCHED VENDOR ITEM'
                                        : 'MATCHED PRICEBOOK ITEM'}
                                </Text>

                                <Grid
                                    templateColumns="max-content auto"
                                    gap="1"
                                    alignItems="center"
                                >
                                    <Text variant="eyebrow" size="small">
                                        Code
                                    </Text>
                                    <Text size="small">{replacementPart.match.code}</Text>
                                    <Text variant="eyebrow" size="small">
                                        Name
                                    </Text>
                                    <Text size="small">{replacementPart.match.name}</Text>
                                    <Text variant="eyebrow" size="small">
                                        Vendor
                                    </Text>
                                    <Text size="small">{replacementPart.match.supplier_name}</Text>
                                </Grid>
                            </Fragment>
                        ) : (
                            <Chip label="No match in Pricebook" />
                        )}

                        {replacementPart.match && (
                            <Flex justifyContent="space-between">
                                <KeyValuePair label="match quality" value={matchQuality} />

                                {hasAnyPermissions &&
                                    [1, 2, 3].includes(replacementPart.match.type) && (
                                        <Button
                                            icon={IconAdd}
                                            appearance="primary"
                                            size="small"
                                            onClick={handleCardClick}
                                        />
                                    )}
                            </Flex>
                        )}
                    </Flex>
                </Card>

                {/* Fullscreen Modal */}
                {modalOpen && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            background: 'rgba(0,0,0,0.9)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 9999,
                        }}
                        onClick={() => setModalOpen(false)}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        <img
                            ref={imgRef}
                            src={replacementPart.imageUrl}
                            alt={replacementPart.label}
                            style={{
                                maxWidth: '90vw',
                                maxHeight: '90vh',
                                transform: `scale(${scale})`,
                                transition: 'transform 0.2s',
                                touchAction: 'none',
                            }}
                            onClick={e => e.stopPropagation()}
                        />
                    </div>
                )}
            </Fragment>
        );
    }
);

export const KeyValuePair: FC<{
    label: string;
    value: ReactNode;
    direction?: FlexProps['direction'];
}> = ({ label, value, direction }) => {
    return (
        <Flex direction={direction ?? 'column'} gap="2">
            <Text variant="eyebrow" size="small">
                {label}
            </Text>
            <Text size="small">{value}</Text>
        </Flex>
    );
};
